import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { X, CheckCircle, Sparkles } from "lucide-react";
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";
import {
  activateFreeTrial,
  closeSuscriptionModal,
  setActivateFreeTrial,
} from "../../../store/actions/profileActions";
import { loadUserData } from "../../../store/actions";
import styles from "./FreeTrialModal.module.scss";

const modalVariants = {
  hidden: { opacity: 0, scale: 1.1, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { delay: 0.1, duration: 0.3, ease: "easeOut" },
  },
};

const BENEFITS = [
  "Interactuar con la IA de Edify de forma ilimitada",
  "Publicar productos/servicios en la página principal",
  "Crear proyectos de Obra Privada y obtener cotizaciones",
  "Postularte en publicaciones de otros usuarios y enviar presupuestos",
  "Acceder a toda la información en Obras Públicas",
  "Crear y cotizar solicitudes de Listado de Materiales",
  "Dar y recibir calificaciones a todos los usuarios",
  "Atención personalizada para consultas",
  "Mailing de difusión a base de datos (una vez al mes)",
];

interface FreeTrialModalProps {
  closeSuscriptionModal: () => void;
  activateFreeTrial: () => void;
  loadUserData: () => void;
  setActivateFreeTrial: (value: boolean) => void;
  userData: {
    free_trial?: {
      early_adopter?: boolean;
    };
  };
  isFreeTrialSuccess: boolean;
  isFromNav?: boolean;
}

const FreeTrialModal: React.FC<FreeTrialModalProps> = ({
  closeSuscriptionModal,
  activateFreeTrial,
  loadUserData,
  setActivateFreeTrial,
  userData,
  isFreeTrialSuccess,
  isFromNav,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDashboard] = useState(location.pathname === "/dashboard");
  const isEarlyAdopter = userData?.free_trial?.early_adopter ?? false;

  const onStartFreeTrial = () => {
    setActivateFreeTrial(true);
    activateFreeTrial();
  };

  const handleClose = () => {
    if (!isDashboard) {
      navigate("/dashboard");
    }
    if (isFreeTrialSuccess) {
      loadUserData();
    }
    if (isFromNav) {
      activateFreeTrial();
      loadUserData();
    }
    setActivateFreeTrial(false);
    closeSuscriptionModal();
  };

  return (
    <ModalOverlay isOpen={true} onClose={handleClose}>
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.modalBody}>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Cerrar modal"
          >
            <X size={20} />
          </button>

          <div className={styles.content}>
            <div className={styles.iconContainer}>
              {isFreeTrialSuccess ? (
                <CheckCircle size={48} className={styles.successIcon} />
              ) : (
                <Sparkles size={48} className={styles.icon} />
              )}
            </div>

            {isFreeTrialSuccess ? (
              <>
                <h3 className={styles.title}>
                  {isEarlyAdopter ? "90 días activados" : "60 días activados"}
                </h3>
                <p className={styles.bodyText}>
                  Obtienes los siguientes beneficios
                </p>
                <ul className={styles.benefitsList}>
                  {BENEFITS.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
                <p className={styles.importantNote}>
                  <span className={styles.important}>¡Importante!</span> No se
                  necesita tarjeta de crédito
                </p>
                <p className={styles.disclaimer}>
                  Una vez finalizado el período de prueba el plan se cancela
                  automáticamente. Vas a poder ingresar y chequear nuevas
                  funcionalidades pero no vas a poder interactuar con los demás
                  usuarios.
                </p>
              </>
            ) : (
              <>
                <h3 className={styles.title}>Contenido Premium</h3>
                <p className={styles.bodyText}>
                  {isEarlyAdopter
                    ? "Muchas gracias por ser uno de los usuarios iniciales de Edify. Lamentablemente el tiempo de uso libre ha terminado. Debés suscribirte para acceder al contenido."
                    : "Comienza tus 60 días de prueba gratis y accedé a los siguientes beneficios:"}
                </p>
                {isEarlyAdopter && (
                  <p className={styles.earlyAdopterNote}>
                    Además, por ser un usuario inicial tenés el beneficio de{" "}
                    <strong>90 días gratis</strong> por única vez.
                  </p>
                )}
                <ul className={styles.benefitsList}>
                  {BENEFITS.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
                <p className={styles.importantNote}>
                  <span className={styles.important}>¡Importante!</span> No se
                  necesita tarjeta de crédito
                </p>
                <p className={styles.disclaimer}>
                  Una vez finalizado el período de prueba el plan se cancela
                  automáticamente. Vas a poder ingresar y chequear nuevas
                  funcionalidades pero no vas a poder interactuar con los demás
                  usuarios.
                </p>
              </>
            )}
          </div>

          <div className={styles.options}>
            {isFreeTrialSuccess ? (
              <button
                className={styles.primaryButton}
                onClick={handleClose}
                type="button"
              >
                ¡Bienvenido a Edify Premium!
              </button>
            ) : (
              <>
                <button
                  className={styles.primaryButton}
                  onClick={onStartFreeTrial}
                  type="button"
                >
                  Comenzar Free Trial
                </button>
                <button
                  className={styles.secondaryButton}
                  onClick={handleClose}
                  type="button"
                >
                  {isDashboard ? "Volver" : "Volver al dashboard"}
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

const mapStateToProps = (state: {
  general: { userData: FreeTrialModalProps["userData"] };
  profile: { isFreeTrialSuccess: boolean };
}) => ({
  userData: state.general.userData,
  isFreeTrialSuccess: state.profile.isFreeTrialSuccess,
});

export default connect(mapStateToProps, {
  closeSuscriptionModal,
  activateFreeTrial,
  loadUserData,
  setActivateFreeTrial,
})(FreeTrialModal);
